import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  name: string;
  included: boolean;
  description?: string;
}

interface PricingCardProps {
  name: string;
  description: string;
  price: string;
  period: string;
  features: Feature[];
  isPopular?: boolean;
  ctaText: string;
  ctaLink: string;
  badge?: string;
  className?: string;
}

const PricingCard = ({
  name,
  description,
  price,
  period,
  features,
  isPopular = false,
  ctaText,
  ctaLink,
  badge,
  className = ''
}: PricingCardProps) => {
  return (
    <div
      className={`relative rounded-2xl border transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow ${
        isPopular
          ? 'border-primary bg-gradient-to-b from-primary/5 to-transparent scale-105' :'border-border bg-card hover:border-primary/50'
      } ${className}`}
    >
      {badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-orange-glow">
          {badge}
        </div>
      )}
      
      <div className="p-8">
        <h3 className="text-2xl font-display font-bold text-foreground mb-2">
          {name}
        </h3>
        <p className="text-muted-foreground mb-6">{description}</p>
        
        <div className="mb-8">
          <div className="flex items-baseline">
            <span className="text-5xl font-display font-bold text-foreground">
              {price}
            </span>
            <span className="text-muted-foreground ml-2">{period}</span>
          </div>
        </div>
        
        <Link
          href={ctaLink}
          className={`block w-full py-3 px-6 rounded-lg font-semibold text-center transition-all duration-300 mb-8 button-3d ${
            isPopular
              ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-3d hover:shadow-3d-hover'
              : 'bg-muted text-foreground hover:bg-primary hover:text-primary-foreground shadow-silver hover:shadow-orange-glow'
          }`}
        >
          {ctaText}
        </Link>
        
        <div className="space-y-4">
          <p className="text-sm font-semibold text-foreground mb-4">Wat is inbegrepen:</p>
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <Icon
                name={feature.included ? 'CheckIcon' : 'XMarkIcon'}
                size={20}
                className={feature.included ? 'text-success mt-0.5' : 'text-muted-foreground mt-0.5'}
              />
              <div className="flex-1">
                <p className={`text-sm ${feature.included ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {feature.name}
                </p>
                {feature.description && (
                  <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingCard;